import React, { useState, useMemo } from "react";
import type { MediaGalleryBlock, MediaGalleryItem } from "@/types/content";
import { useCarousel } from "@/hooks/useCarousel";
import { responsiveCmsImage } from "@/utils/cmsImage";
import styles from "./MediaGallery.module.css";

/**
 * The slide is the layout's 2fr column: 725px of the 1136px container in a
 * full-width block, 1.5fr of 2.5fr below 1024px, full width below 768px.
 */
const SLIDE_IMAGE_SIZES =
  "(max-width: 768px) 100vw, (max-width: 1024px) 60vw, (max-width: 1200px) 66vw, 725px";

export interface MediaGalleryProps {
  block: MediaGalleryBlock;
  className?: string;
}

/** Left-pointing chevron arrow */
const ArrowLeft: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

/** Right-pointing chevron arrow */
const ArrowRight: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 6 15 12 9 18" />
  </svg>
);

/** Play triangle icon for video overlay */
const PlayIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);

interface ParsedVideo {
  embedUrl: string;
  /** Provider-derived thumbnail. Null when the provider has no public thumbnail
   *  URL pattern (e.g. Vimeo, which requires an oEmbed call). */
  thumbnailUrl: string | null;
}

/**
 * Parse a YouTube or Vimeo URL into an embeddable URL and, where the provider
 * exposes one, a thumbnail URL. Returns null if the URL is not recognized.
 */
function parseVideoUrl(videoUrl: string): ParsedVideo | null {
  const ytMatch = videoUrl.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  if (ytMatch) {
    const id = ytMatch[1];
    return {
      embedUrl: `https://www.youtube-nocookie.com/embed/${id}?autoplay=1`,
      thumbnailUrl: `https://img.youtube.com/vi/${id}/hqdefault.jpg`,
    };
  }

  const vimeoMatch = videoUrl.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return {
      embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`,
      thumbnailUrl: null,
    };
  }

  return null;
}

interface SlideProps {
  item: MediaGalleryItem;
}

/**
 * Individual carousel slide - renders either an image or video thumbnail with play overlay
 */
const Slide: React.FC<SlideProps> = ({ item }) => {
  const [playing, setPlaying] = useState(false);

  const parsedVideo = useMemo(
    () => (item.videoUrl ? parseVideoUrl(item.videoUrl) : null),
    [item.videoUrl]
  );

  if (item.mediaType === "video" && parsedVideo) {
    if (playing) {
      return (
        <div className={styles.slide}>
          <iframe
            className={styles.videoEmbed}
            src={parsedVideo.embedUrl}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            title={item.caption || "Video"}
          />
          {item.caption && <p className={styles.caption}>{item.caption}</p>}
        </div>
      );
    }

    const thumbnailUrl = item.image?.url ?? parsedVideo.thumbnailUrl;

    return (
      <div
        className={`${styles.slide} ${styles.videoSlide}`}
        onClick={() => setPlaying(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setPlaying(true);
          }
        }}
        aria-label={`Play video${item.caption ? `: ${item.caption}` : ""}`}
      >
        {thumbnailUrl ? (
          <img
            {...responsiveCmsImage(
              thumbnailUrl,
              SLIDE_IMAGE_SIZES,
              item.image?.width
            )}
            alt={item.caption || "Video thumbnail"}
            className={styles.slideImage}
            loading="lazy"
          />
        ) : (
          <div className={styles.slideImage} style={{ background: "#333" }} />
        )}
        <div className={styles.playOverlay}>
          <PlayIcon />
        </div>
        {item.caption && <p className={styles.caption}>{item.caption}</p>}
      </div>
    );
  }

  // Image type
  return (
    <div className={styles.slide}>
      {item.image?.url ? (
        <img
          {...responsiveCmsImage(
            item.image.url,
            SLIDE_IMAGE_SIZES,
            item.image.width
          )}
          alt={item.caption || "Gallery image"}
          className={styles.slideImage}
          loading="lazy"
        />
      ) : (
        <div className={styles.slideImage} style={{ background: "#333" }} />
      )}
      {item.caption && <p className={styles.caption}>{item.caption}</p>}
    </div>
  );
};

/**
 * Media Gallery - carousel of images and videos with heading and navigation controls.
 * Split layout: heading/description on left, single large slide on right.
 * Arrows centered at bottom, dots at bottom-right.
 */
export const MediaGallery: React.FC<MediaGalleryProps> = ({
  block,
  className,
}) => {
  const { heading, description, items } = block;

  const {
    currentIndex,
    goTo,
    goPrev,
    goNext,
    canGoPrev,
    canGoNext,
    translateX,
  } = useCarousel({ itemCount: items.length });

  if (!items || items.length === 0) {
    return null;
  }

  const containerClasses = [styles.section, className]
    .filter(Boolean)
    .join(" ");

  return (
    <section
      className={containerClasses}
      data-component="media-gallery"
      aria-label={heading || "Media gallery"}
    >
      <div className={styles.container}>
        <div className={styles.layout}>
          {/* Left side: text only */}
          <div className={styles.textContent}>
            {heading && <h2 className={styles.heading}>{heading}</h2>}
            {description && <p className={styles.description}>{description}</p>}
          </div>

          {/* Right side: single large slide */}
          <div className={styles.carouselWrapper}>
            <div
              className={styles.carouselTrack}
              style={{ transform: `translateX(${translateX})` }}
            >
              {items.map((item, index) => (
                <Slide key={item.id || index} item={item} />
              ))}
            </div>
          </div>

          {/* Controls row: arrows centered, dots right */}
          <div className={styles.controls}>
            <div className={styles.arrows}>
              <button
                className={styles.arrowButton}
                onClick={goPrev}
                disabled={!canGoPrev}
                aria-label="Previous slide"
              >
                <ArrowLeft />
              </button>
              <button
                className={styles.arrowButton}
                onClick={goNext}
                disabled={!canGoNext}
                aria-label="Next slide"
              >
                <ArrowRight />
              </button>
            </div>

            <div
              className={styles.dots}
              role="tablist"
              aria-label="Slide navigation"
            >
              {items.map((item, index) => (
                <button
                  key={item.id || index}
                  className={`${styles.dot} ${index === currentIndex ? styles.dotActive : ""}`}
                  onClick={() => goTo(index)}
                  role="tab"
                  aria-selected={index === currentIndex}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MediaGallery;
